import { doTimesOverlap } from '@court-sync/shared';
import { timeToMinutes } from './timeFormat';

/** Items that have a time range for overlap detection */
export interface TimeRanged {
  startTime: string;
  endTime: string;
}

export function timeRangesOverlap(a: TimeRanged, b: TimeRanged): boolean {
  return doTimesOverlap(a.startTime, a.endTime, b.startTime, b.endTime);
}

/**
 * Partition items into connected overlap groups (same algorithm as WeeklySchedule / TermScheduleView).
 */
export function getOverlappingTimeGroups<T extends TimeRanged>(
  items: T[],
  getKey: (item: T) => string
): T[][] {
  if (items.length === 0) return [];

  const groups: T[][] = [];
  const processed = new Set<string>();

  items.forEach((item) => {
    const key = getKey(item);
    if (processed.has(key)) return;

    const group: T[] = [item];
    processed.add(key);

    let foundNew = true;
    while (foundNew) {
      foundNew = false;
      items.forEach((other) => {
        const otherKey = getKey(other);
        if (processed.has(otherKey)) return;
        const overlaps = group.some((g) => timeRangesOverlap(g, other));
        if (overlaps) {
          group.push(other);
          processed.add(otherKey);
          foundNew = true;
        }
      });
    }

    groups.push(group);
  });

  return groups;
}

export interface StackedColumnStyle {
  top: string;
  height: string;
  left: string;
  width: string;
}

/**
 * Vertical position + horizontal stacking when multiple items overlap in the same column
 * (same layout rules as WeeklySchedule).
 */
export function getStackedTimeSlotStyle<T extends TimeRanged>(
  item: T,
  columnItems: T[],
  getKey: (item: T) => string,
  earliestTime: number,
  latestTime: number
): StackedColumnStyle {
  const totalRange = latestTime - earliestTime;
  const startMinutes = timeToMinutes(item.startTime);
  const endMinutes = timeToMinutes(item.endTime);
  const topPercent = ((startMinutes - earliestTime) / totalRange) * 100;
  const heightPercent = ((endMinutes - startMinutes) / totalRange) * 100;

  const groups = getOverlappingTimeGroups(columnItems, getKey);
  const group = groups.find((g) => g.some((x) => getKey(x) === getKey(item)));

  if (group && group.length > 1) {
    const groupIndex = group.findIndex((x) => getKey(x) === getKey(item));
    const groupSize = group.length;
    const gapPercent = 0.5;
    const sidePadding = 1;
    const availableWidth = 100 - sidePadding * 2 - gapPercent * (groupSize - 1);
    const widthPercent = availableWidth / groupSize;
    const leftPercent = sidePadding + groupIndex * (widthPercent + gapPercent);
    return {
      top: `${topPercent}%`,
      height: `${heightPercent}%`,
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
    };
  }

  return {
    top: `${topPercent}%`,
    height: `${heightPercent}%`,
    left: '4px',
    width: 'calc(100% - 8px)',
  };
}
